<script setup lang="ts">
/**
 * 文本输入。薄封装原生 input —— 校验交给 el-form，这里只统一外观。
 *
 * `inheritAttrs: false` 不能省。
 *
 * 加 clearable 时给根部套了一层 `<div class="relative">` 装清除按钮。默认情况下
 * `id` / `autocomplete` / `data-test` 会落到那个 **div** 上，于是：
 *   · `<label for="username">` 关联到 div，点标签不聚焦输入框；
 *   · 浏览器不认得这是账号框，自动填充与密码管理器全部失效；
 *   · Playwright 的 `fill('#username')` 直接报「不是 input」。
 * 关掉自动继承、把 `$attrs` 显式绑到内层 input 才是对的。
 */
import { X } from './icons'
import { computed } from 'vue'

defineOptions({ inheritAttrs: false })

import { cn } from '@/lib/utils'

interface Props {
  //: `null` 也收。绑定的对象常常是 OpenAPI 生成的类型（`S['RowFilterIn']` 之类），
  //: 那里的可选字符串字段是 `string | null | undefined` —— Pydantic 的
  //: `str | None` 就是这么出到 schema 里的。不收 `null` 的话每个搜索框都得
  //: 在页面里另建一个本地 `ref<string>` 再手动同步回去，而漏同步的症状是
  //: 「输入框里有字，点搜索却按空条件查」。渲染侧统一 `?? ''`。
  modelValue?: string | number | null
  type?: string
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  maxlength?: number | string
  /** 有值时右侧出现清除按钮。EP 的 clearable 用得很多，保留同名以便迁移。 */
  clearable?: boolean
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'text',
  placeholder: '',
  disabled: false,
  invalid: false,
  maxlength: undefined,
  clearable: false,
  class: '',
})

const emit = defineEmits<{ 'update:modelValue': [value: string]; clear: [] }>()

const showClear = computed(
  () => props.clearable && !props.disabled && String(props.modelValue ?? '') !== '',
)

/**
 * ★ 宽度/伸缩类要落在**外层包装**上，不能只落在 `<input>` 上（2026-08-05）。
 *
 * clearable 的清除按钮要绝对定位，所以根部套了一层 `relative` 的 div。那层
 * 此前恒 `w-full`：调用方写 `class="w-full sm:w-64"` 时，窄的是里面的 input，
 * **外层照样撑满整行** —— 于是在 flex 工具栏里它独占一行，把后面的筛选器挤到
 * 第二行（DESIGN §6.2 要求桌面横排；知识库文档页正是这么坏的）。
 *
 * 为什么只挑宽度类而不是把 `props.class` 整个搬过去：调用方传的东西分两类 ——
 * `w-56`/`flex-1` 是**布局**（属于外层），`pl-10`/`font-mono`/`h-10` 是
 * **输入框自身的样式**（登录页靠 `pl-10` 给内嵌图标让位）。整个搬过去会让
 * 图标压在文字上。所以：布局类复制到外层，`props.class` 在 input 上原样保留。
 */
const LAYOUT_CLASS_RE =
  /(?:^|\s)((?:sm:|md:|lg:|xl:|2xl:)?(?:w-[\w./[\]-]+|max-w-[\w./[\]-]+|min-w-[\w./[\]-]+|flex-1|flex-auto|grow|shrink-0))/g

const wrapperClasses = computed(() => {
  const layout = [...props.class.matchAll(LAYOUT_CLASS_RE)].map((m) => m[1])
  // 一个宽度类都没传时保持老行为（撑满），否则会让既有页面的输入框塌缩。
  // 只认上面那个正则里的 Tailwind 类：传 scoped CSS 类（`.foo { width: 8rem }`）
  // 匹配不到，会走 `w-full` 分支撑满，而 `AppSelect` 是原样拼所以同一行里表现不一致。
  return cn('relative', layout.length ? layout.join(' ') : 'w-full')
})

const classes = computed(() =>
  cn(
    'flex h-8 w-full rounded-lg border border-input bg-background px-3 py-1 text-sm shadow-2xs transition-colors',
    'placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[1px] focus-visible:ring-ring/30 focus-visible:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-50',
    props.invalid && 'border-destructive focus-visible:ring-destructive/20',
    showClear.value && 'pr-8',
    props.class,
  ),
)

function clear(): void {
  emit('update:modelValue', '')
  // 单独发 clear：不少调用方在清空后要立刻重新查询（搜索框），
  // 只靠 update:modelValue 分不清「点了清除」与「删到空」。
  emit('clear')
}
</script>

<template>
  <div :class="wrapperClasses">
    <input
      v-bind="$attrs"
      :type="type"
      :value="modelValue ?? ''"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxlength="maxlength"
      :aria-invalid="invalid || undefined"
      :class="classes"
      @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
    <button
      v-if="showClear"
      type="button"
      class="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
      aria-label="清除"
      title="清除"
      data-test="input-clear"
      @click="clear"
    >
      <X class="size-4" aria-hidden="true" />
    </button>
  </div>
</template>
